import { configureStore } from '@reduxjs/toolkit'
import {
  persistStore,
  persistReducer,
  createTransform,
  createMigrate, // ← add this
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage' // uses localStorage

const normalizePersistedValue = (value, key) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      trimmed === '' ||
      trimmed.toLowerCase() === 'null' ||
      trimmed.toLowerCase() === 'undefined'
    ) {
      return null
    }
    if (key === 'agentData') {
      try {
        return JSON.parse(value)
      } catch (err) {
        return value
      }
    }
  }
  return value
}

const normalizeAgentData = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      trimmed === '' ||
      trimmed.toLowerCase() === 'null' ||
      trimmed.toLowerCase() === 'undefined'
    ) {
      return null
    }
    try {
      return JSON.parse(value)
    } catch (err) {
      return value
    }
  }

  if (typeof value === 'object' && value !== null) {
    const normalized = { ...value }
    if (normalized.agent_id !== undefined) {
      normalized.agent_id = normalizePersistedValue(
        normalized.agent_id,
        'agent_id',
      )
    }
    if (normalized.agentID !== undefined) {
      normalized.agentID = normalizePersistedValue(
        normalized.agentID,
        'agent_id',
      )
    }
    if (normalized.store_id !== undefined) {
      normalized.store_id = normalizePersistedValue(
        normalized.store_id,
        'store_id',
      )
    }
    if (Array.isArray(normalized.StoreID)) {
      normalized.StoreID = normalized.StoreID.map((item) =>
        normalizePersistedValue(item, 'store_id'),
      )
    }
    return normalized
  }

  return value
}

const normalizePersistedId = (value) =>
  normalizePersistedValue(value, 'agent_id')

const idTransform = createTransform(
  (inboundState, key) => normalizePersistedValue(inboundState, key),
  (outboundState, key) => normalizePersistedValue(outboundState, key),
  { whitelist: ['agentData', 'agent_id', 'store_id'] },
)

// ── Persist config ────────────────────────────────────────────────────────────
// Only the keys listed in whitelist will be saved to localStorage.
// Everything else stays in memory only.
const migrations = {
  2: (state) => ({
    ...state,
    // Reset all UI state that should never survive a refresh
    chatProductsReady: false,
    chatProductsLoading: false,
    chatProductFlowStep: null,
    chatSelectedProduct: null,
    chatProductRoutineOpen: false,
    chatProductRoutineReady: false,
    chatProductRoutineData: null,
    chatPhotoPreview: null,
  }),
  3: (state) => ({
    ...state,
    // Anyone rehydrating from pre-v3 localStorage won't have chatFlowType
    // saved yet — default it to null so Chatbot.jsx re-derives it once from
    // agent_id and then locks it in going forward.
    chatFlowType: state.chatFlowType ?? null,
  }),
}

export const persistConfig = {
  key: 'halisi',
  storage,
  version: 3,
  migrate: createMigrate(migrations, { debug: false }),
  transforms: [idTransform],
  whitelist: [
    // Auth
    'agentData',
    'existingUserProfile',
    'isLoggedIn',
    'agent_id',
    'store_id',

    // IMPORTANT:
    // Persist the user's selected language.
    'language',

    // Product/chat session state
    'productRecommendations',
    'chatMessages',
    'chatQuestionStep',
    'chatCollectedAnswers',
    'chatAttemptCounts',
    'chatValidationState',
    'chatHairRetryCount',
    'chatHairNeedsRetry',
    'chatFlowType',
  ],
}

// ── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  consent_flag: true,
  cookieConsent: null,
  isLoggedIn: false,
  agentData: null,
  existingUserProfile: null,
  distributor_id: null,
  distributor_info: null,
  language: 'en',
  trialExpired: false,
  isSubmit: false,
  childage: null,
  logoutProfile: null,
  saveSelectedButtons: [],
  hair_class: null,
  hair_state: null,
  hair_goals: null,
  hair_challenges: null,
  scalp_challenges: null,
  categories: [],
  contraindications: null,
  selectedCategory: '',
  productRecommendations: null,
  productIndex: null,
  savedProduct: null,
  vendorDetails: null,
  ticketNumber: null,
  capturedImage: null,
  diagnosticsLLMText: '',
  productLLMText: '',
  saved_answers: null,
  saved_constraints: null,
  segmentedImage: null,
  realHairSegment: '',
  lastAction: null,
  isAnalyzeDisabled: false,
  reason: '',
  store_id: null,
  selected_distributor_logo: null,
  selected_distributor_name: null,
  selected_distributor_type: null,
  enableHairSegmentation: true,
  questionnaire: {},
  translationsData: {},
  agent_id: null,

  // ── Chat state ──────────────────────────────────────────────────────────────
  chatMessages: [],
  chatQuestionStep: -1,
  chatCollectedAnswers: [],
  chatPhotoPreview: null,
  chatProductsReady: false,
  chatProductsLoading: false,
  chatProductFlowStep: null,
  chatSelectedProduct: null,
  chatProductRoutineOpen: false,
  chatProductRoutineReady: false,
  chatProductRoutineData: null,
  chatAttemptCounts: {},
  chatValidationState: {},
  chatHairRetryCount: 0,
  chatHairNeedsRetry: false,
  // 'new' | 'existing' | null — decided once per conversation by Chatbot.jsx
  // and persisted, so it survives page changes / logout-login instead of
  // being re-derived from agent_id (which can flip mid-conversation).
  chatFlowType: null,
  chatCompletedNewUserFlow: false,
  segmentedRawImage: null,
}

// ── Reducer ───────────────────────────────────────────────────────────────────
const reducer = (state = initialState, action) => {
  switch (action.type) {
    // ── Existing app cases (unchanged) ────────────────────────────────────────
    case 'SET_AGENT_DATA':
      return { ...state, agentData: normalizeAgentData(action.payload) }
    case 'SET_EXISTING_USER_PROFILE':
      return { ...state, existingUserProfile: action.payload }
    case 'SET_AGENT_ID':
      return { ...state, agent_id: normalizePersistedId(action.payload) }
    case 'SET_LANGUAGE':
      return {
        ...state,
        language:
          typeof action.payload === 'string'
            ? action.payload.trim().toLowerCase()
            : state.language,
      }
    case 'SET_TRIAL_EXPIRY':
      return { ...state, trialExpired: action.payload }
    case 'SET_QUESTIONNAIRE':
      return { ...state, questionnaire: action.payload }
    case 'SET_CONTRAINDICATIONS':
      return { ...state, contraindications: action.payload }
    case 'SET_TRANSLATIONS_DATA':
      return { ...state, translationsData: action.payload }
    case 'SET_CAPTURED_IMAGE':
      return { ...state, capturedImage: action.payload }
    case 'SET_IS_SUBMIT':
      return { ...state, isSubmit: action.payload }
    case 'AGENT_AUTH_PROFILE':
      return { ...state, profile: action.payload }
    case 'LOGOUT_AGENT_PROFILE':
      return { ...state, profile: initialState.profile }
    case 'LOGIN_STATUS':
      return { ...state, isLoggedIn: action.payload }
    case 'SET_CONSENT_FLAG':
      return { ...state, consent_flag: action.payload }
    case 'SET_COOKIE_CONSENT':
      return { ...state, cookieConsent: action.payload }
    case 'SAVE_CHILD_AGE_RESPONSE':
      return { ...state, childage: action.payload }
    case 'SAVED_HAIR_CLASS':
      return { ...state, hair_class: action.payload }
    case 'SAVED_HAIR_STATE':
      return { ...state, hair_state: action.payload }
    case 'SAVED_HAIR_GOALS':
      return { ...state, hair_goals: action.payload }
    case 'SAVED_HAIR_CHALLENGES':
      return { ...state, hair_challenges: action.payload }
    case 'SAVED_SCALP_CHALLENGES':
      return { ...state, scalp_challenges: action.payload }
    case 'SET_PRODUCT_CATEGORIES':
      return { ...state, categories: action.payload }
    case 'SAVE_CONTRAINDICATIONS':
      return { ...state, contraindications: action.payload }
    case 'SELECTED_CATEGORY':
      return { ...state, selectedCategory: action.payload }
    case 'SET_PRODUCT_RECOMMENDATIONS':
      return { ...state, productRecommendations: action.payload }
    case 'PRODUCT_INDEX':
      return { ...state, productIndex: action.payload }
    case 'SELECTED_DISTRIBUTOR_LOGO':
      return { ...state, selected_distributor_logo: action.payload }
    case 'SELECTED_DISTRIBUTOR_NAME':
      return { ...state, selected_distributor_name: action.payload }
    case 'SELECTED_DISTRIBUTOR_TYPE':
      return { ...state, selected_distributor_type: action.payload }
    case 'SAVE_SELECTED_PRODUCT':
      return { ...state, savedProduct: action.payload }
    case 'SET_VENDOR_DETAILS':
      return { ...state, vendorDetails: action.payload }
    case 'TICKET_NUMBER':
      return { ...state, ticketNumber: action.payload }
    case 'DIAGNOSTICS_LLM_TEXT':
      return { ...state, diagnosticsLLMText: action.payload }
    case 'PRODUCT_LLM_TEXT':
      return { ...state, productLLMText: action.payload }
    case 'CLEAR_SAVED_PRODUCT':
      return { ...state, savedProduct: initialState.savedProduct }
    case 'SAVED_FORM_ANSWERS':
      return { ...state, saved_answers: action.payload }
    case 'SAVED_CONSTRAINTS':
      return { ...state, saved_constraints: action.payload }
    case 'CLEAR_DIAGNOSTICS_LLM':
      return { ...state, diagnosticsLLMText: initialState.diagnosticsLLMText }
    case 'CLEAR_PRODUCT_LLM':
      return { ...state, productLLMText: initialState.productLLMText }
    case 'SET_SEGMENTATION_RESPONSE_IMG_SRC':
      return { ...state, segmentedImage: action.payload }
    case 'SET_REAL_HAIR_SEGMENT':
      return { ...state, realHairSegment: action.payload }
    case 'SET_ANALYSE_BUTTON_STATUS':
      return { ...state, isAnalyzeDisabled: action.payload }
    case 'SET_LAST_ACTION':
      return { ...state, lastAction: action.payload }
    case 'SET_REASON':
      return { ...state, reason: action.payload }
    case 'CLEAR_CAPTURED_IMAGE':
      return { ...state, capturedImage: initialState.capturedImage }
    case 'CLEAR_REASON':
      return { ...state, reason: initialState.reason }
    case 'DISTRIBUTOR_ID':
      return { ...state, distributor_id: action.payload }
    case 'STORE_ID':
      return { ...state, store_id: normalizePersistedId(action.payload) }
    case 'DISTRIBUTOR_INFO':
      return { ...state, distributor_info: action.payload }
    case 'CLEAR_HAIR_MASK':
      return { ...state, realHairSegment: initialState.realHairSegment }
    case 'ENABLE_HAIR_SEGMENTATION':
      return { ...state, enableHairSegmentation: action.payload }
    case 'UPGRADE_PLAN':
      return {
        ...state,
        agentData: { ...state.agentData, plan: action.payload },
      }

    // ── Chat cases ────────────────────────────────────────────────────────────
    case 'CHAT_SET_MESSAGES':
      return { ...state, chatMessages: action.payload }
    case 'CHAT_SET_QUESTION_STEP':
      return { ...state, chatQuestionStep: action.payload }
    case 'CHAT_SET_COLLECTED_ANSWERS':
      return { ...state, chatCollectedAnswers: action.payload }
    case 'CHAT_SET_PHOTO_PREVIEW':
      return { ...state, chatPhotoPreview: action.payload }
    case 'CHAT_SET_PRODUCTS_READY':
      return { ...state, chatProductsReady: action.payload }
    case 'CHAT_SET_PRODUCTS_LOADING':
      return { ...state, chatProductsLoading: action.payload }
    case 'CHAT_SET_PRODUCT_FLOW_STEP':
      return { ...state, chatProductFlowStep: action.payload }
    case 'CHAT_SET_SELECTED_PRODUCT':
      return { ...state, chatSelectedProduct: action.payload }
    case 'CHAT_SET_PRODUCT_ROUTINE_OPEN':
      return { ...state, chatProductRoutineOpen: action.payload }
    case 'CHAT_SET_PRODUCT_ROUTINE_READY':
      return { ...state, chatProductRoutineReady: action.payload }
    case 'CHAT_SET_PRODUCT_ROUTINE_DATA':
      return { ...state, chatProductRoutineData: action.payload }
    case 'CHAT_SET_ATTEMPT_COUNTS':
      return { ...state, chatAttemptCounts: action.payload }
    case 'CHAT_SET_VALIDATION_STATE':
      return { ...state, chatValidationState: action.payload }
    case 'CHAT_SET_HAIR_RETRY_COUNT':
      return { ...state, chatHairRetryCount: action.payload }
    case 'CHAT_SET_HAIR_NEEDS_RETRY':
      return { ...state, chatHairNeedsRetry: action.payload }
    case 'CHAT_SET_FLOW_TYPE':
      return { ...state, chatFlowType: action.payload }
    //New user flow completion status
    case 'CHAT_SET_COMPLETED_NEW_USER_FLOW':
      return { ...state, chatCompletedNewUserFlow: action.payload }

    case 'SET_SEGMENTED_RAW_IMAGE':
      return { ...state, segmentedRawImage: action.payload }

    // Resets only chat state, leaves all other app state untouched
    case 'CHAT_RESET':
      return {
        ...state,
        chatMessages: [],
        chatQuestionStep: -1,
        chatCollectedAnswers: [],
        chatPhotoPreview: null,
        chatProductsReady: false,
        chatProductsLoading: false,
        chatProductFlowStep: null,
        chatSelectedProduct: null,
        chatProductRoutineOpen: false,
        chatProductRoutineReady: false,
        chatProductRoutineData: null,
        chatAttemptCounts: {},
        chatValidationState: {},
        chatHairRetryCount: 0,
        chatHairNeedsRetry: false,
        chatFlowType: null,
        chatCompletedNewUserFlow: false,
      }

    // Full app reset — used when a user deletes their profile.
    // Resets every key back to initialState, but preserves redux-persist's
    // internal bookkeeping key (_persist) so persist doesn't break.
    case 'RESET_APP':
      return { ...initialState, _persist: state._persist }

    default:
      return state
  }
}

// ── Wrap reducer with persist ─────────────────────────────────────────────────
const persistedReducer = persistReducer(persistConfig, reducer)

// ── Create store ──────────────────────────────────────────────────────────────
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // redux-persist dispatches these non-serializable actions internally
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
})

export const persistor = persistStore(store)
export default store

// ── Existing action creators (unchanged) ──────────────────────────────────────
export const clearDiagnosticsLLM = () => ({ type: 'CLEAR_DIAGNOSTICS_LLM' })
export const clearProductLLM = () => ({ type: 'CLEAR_PRODUCT_LLM' })
export const clearSavedProduct = () => ({ type: 'CLEAR_SAVED_PRODUCT' })
export const setProductIndex = (data) => ({
  type: 'PRODUCT_INDEX',
  payload: data,
})
export const clearFormData = () => ({ type: 'CLEAR_FORM_DATA' })
export const clearFormDataLivestock = () => ({
  type: 'CLEAR_FORM_DATA_LIVESTOCK',
})
export const logoutProfile = () => ({ type: 'LOGOUT_AGENT_PROFILE' })
export const saveSelectedButtons = () => ({ type: 'SAVE_SELECTED_BUTTONS' })
export const setCapturedImage = (image) => ({
  type: 'SET_CAPTURED_IMAGE',
  payload: image,
})
export const setSegmentationResponseImgSrc = (src) => ({
  type: 'SET_SEGMENTATION_RESPONSE_IMG_SRC',
  payload: src,
})
export const clearCapturedImage = () => ({ type: 'CLEAR_CAPTURED_IMAGE' })
export const clearReason = () => ({ type: 'CLEAR_REASON' })
export const setRealHairSegment = (src) => ({
  type: 'SET_REAL_HAIR_SEGMENT',
  payload: src,
})
export const setSavedProduct = (savedProduct) => ({
  type: 'SAVE_SELECTED_PRODUCT',
  payload: savedProduct,
})
export const clearHairMask = () => ({ type: 'CLEAR_HAIR_MASK' })
export const clearDistributorLogo = () => ({
  type: 'SELECTED_DISTRIBUTOR_LOGO',
})
export const setAnalyseButtonStatus = (isAnalyzeDisabled) => ({
  type: 'SET_ANALYSE_BUTTON_STATUS',
  payload: isAnalyzeDisabled,
})
export const setReason = (reason) => ({ type: 'SET_REASON', payload: reason })
export const setAgentData = (agentData) => ({
  type: 'SET_AGENT_DATA',
  payload: agentData,
})
export const upgradePlan = (newPlan) => ({
  type: 'UPGRADE_PLAN',
  payload: newPlan,
})

// ── Chat action creators ──────────────────────────────────────────────────────
export const chatSetMessages = (payload) => ({
  type: 'CHAT_SET_MESSAGES',
  payload,
})
export const chatSetQuestionStep = (payload) => ({
  type: 'CHAT_SET_QUESTION_STEP',
  payload,
})
export const chatSetCollectedAnswers = (payload) => ({
  type: 'CHAT_SET_COLLECTED_ANSWERS',
  payload,
})
export const chatSetPhotoPreview = (payload) => ({
  type: 'CHAT_SET_PHOTO_PREVIEW',
  payload,
})
export const chatSetProductsReady = (payload) => ({
  type: 'CHAT_SET_PRODUCTS_READY',
  payload,
})
export const chatSetProductsLoading = (payload) => ({
  type: 'CHAT_SET_PRODUCTS_LOADING',
  payload,
})
export const chatSetProductFlowStep = (payload) => ({
  type: 'CHAT_SET_PRODUCT_FLOW_STEP',
  payload,
})
export const chatSetSelectedProduct = (payload) => ({
  type: 'CHAT_SET_SELECTED_PRODUCT',
  payload,
})
export const chatSetRoutineOpen = (payload) => ({
  type: 'CHAT_SET_PRODUCT_ROUTINE_OPEN',
  payload,
})
export const chatSetRoutineReady = (payload) => ({
  type: 'CHAT_SET_PRODUCT_ROUTINE_READY',
  payload,
})
export const chatSetRoutineData = (payload) => ({
  type: 'CHAT_SET_PRODUCT_ROUTINE_DATA',
  payload,
})
export const chatSetAttemptCounts = (payload) => ({
  type: 'CHAT_SET_ATTEMPT_COUNTS',
  payload,
})
export const chatSetValidationState = (payload) => ({
  type: 'CHAT_SET_VALIDATION_STATE',
  payload,
})
export const chatSetHairRetryCount = (payload) => ({
  type: 'CHAT_SET_HAIR_RETRY_COUNT',
  payload,
})
export const chatSetHairNeedsRetry = (payload) => ({
  type: 'CHAT_SET_HAIR_NEEDS_RETRY',
  payload,
})
export const chatSetFlowType = (payload) => ({
  type: 'CHAT_SET_FLOW_TYPE',
  payload,
})
// New user flow completion status
export const chatSetCompletedNewUserFlow = (payload) => ({
  type: 'CHAT_SET_COMPLETED_NEW_USER_FLOW',
  payload,
})

export const chatReset = () => ({ type: 'CHAT_RESET' })

// Full state reset — call this when a user deletes their profile.
export const resetApp = () => ({ type: 'RESET_APP' })

export const setSegmentedRawImage = (payload) => ({
  type: 'SET_SEGMENTED_RAW_IMAGE',
  payload,
})
